import React, { useState, useEffect } from 'react';
import { Menu, X, ChevronDown } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useSmartNav } from '../../hooks/useSmartNav';

// Data-driven site navigation. Mirrors the target IA (see /app/memory/ROADMAP.md).
// 2026-08-25: Desi-approved pages link to their clean SEO route (never /mockup).
// Unapproved/deferred pages stay commented out until greenlit.
const NAV = [
  {
    label: 'How It Works',
    to: '/how-it-works',
    items: [
      { label: 'See the live demo', to: '/how-it-works', desc: 'Watch Archen™ read a real email thread.' },
      { label: 'Zero-Entry Project Management', to: '/zero-entry-project-management', desc: 'Run projects without maintaining a tool.' },
      { label: 'Automatic Project Documentation', to: '/automatic-project-documentation', desc: 'Your inbox becomes the record.' },
    ],
  },
  {
    label: 'Product',
    to: '/product',
    items: [
      { label: 'What Is Project Memory Software?', to: '/what-is-project-memory-software', desc: 'The definition, and why design studios use it.' },
      { label: 'Overview', to: '/product', desc: 'Everything one record powers, in one place.' },
      { label: 'Project Memory', to: '/project-memory-software', flag: 'Flagship', desc: 'The living history of every project decision.' },
      { label: 'Client Approvals', to: '/interior-design-client-approval-software', desc: 'Every sign-off, captured from the email it happened in.' },
      { label: 'Client Communication', to: '/interior-design-client-communication-software', desc: 'Client threads turned into a searchable record.' },
      { label: 'Client Portal', to: '/client-portal-software-interior-designers', desc: 'A client view that updates itself.' },
      { label: 'Decision Documentation', to: '/design-decision-documentation-software', desc: "Every choice logged as it's made, not after." },
      { label: 'Knowledge Management', to: '/design-firm-knowledge-management', desc: "Your firm's know-how, built automatically." },
      { label: "Buyer's Guide", to: '/best-interior-design-project-management-software', desc: 'How to choose the right tool for your studio.' },
    ],
  },
  // Hidden until launch (user will reintroduce later)
  // {
  //   label: 'For Your Firm',
  //   items: [
  //     { label: 'Interior Designers', to: '/mockup/lp-002' },
  //     { label: 'Millwork', to: '/mockup/lp-011' },
  //     { label: 'Cabinet Shops', to: '/mockup/lp-012' },
  //     { label: 'Property & Facilities', to: '/mockup/lp-013' },
  //     { label: "Buyer's Guide", to: '/mockup/lp-006' },
  //   ],
  // },
  { label: 'Pricing', to: '/pricing' },
  { label: 'Insights', to: '/insights' },
  { label: 'Founding Designer', to: '/foundingdesigner' },
];

const SoonBadge = () => (
  <span className="text-[9px] uppercase tracking-[0.14em] text-[#6B655C] border border-[#E0D6C6] rounded-full px-2 py-0.5">Soon</span>
);
const FlagBadge = ({ label }) => (
  <span className="text-[9px] uppercase tracking-[0.14em] text-[#5B7247] bg-[#E7EEE0] rounded-full px-2 py-0.5">{label}</span>
);

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const goTo = useSmartNav();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 12);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNav = (e, href) => {
    e.preventDefault();
    goTo(href, () => setIsMobileMenuOpen(false));
  };

  const { pathname } = useLocation();
  const isActive = (group) => {
    if (group.items && group.items.some((it) => it.to && pathname === it.to)) return true;
    if (group.to) return pathname === group.to || (group.to !== '/' && pathname.startsWith(`${group.to}/`));
    return false;
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-[#FDF8F3] border-b border-[#E8E0D8]' : 'bg-transparent border-b border-transparent'
      }`}
    >
      <div className="max-w-[1240px] mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <a href="/" onClick={(e) => handleNav(e, '/')} className="flex items-center gap-2.5" data-testid="logo-link">
          <img src="https://customer-assets.emergentagent.com/job_archen-studio/artifacts/kz0rmfk8_logo.svg" alt="Archen™" className="h-7 w-auto" />
          <span className="text-[#2D2520] text-xl tracking-tight" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>Archen™</span>
        </a>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-8">
          {NAV.map((group) =>
            group.items ? (
              <div
                key={group.label}
                className="relative"
                onMouseEnter={() => setOpenMenu(group.label)}
                onMouseLeave={() => setOpenMenu(null)}
              >
                <button
                  data-testid={`nav-${group.label.toLowerCase().replace(/\s+/g, '-')}`}
                  onClick={(e) => group.to && handleNav(e, group.to)}
                  aria-current={isActive(group) ? 'page' : undefined}
                  className={`flex items-center gap-1 text-[14px] font-medium tracking-wide transition-colors border-b-2 pb-0.5 ${group.to ? 'cursor-pointer' : ''} ${
                    isActive(group)
                      ? 'text-[#1F1812] border-[#7A5A2E]'
                      : `text-[#5C4F42] hover:text-[#2D2520] border-transparent ${group.to ? 'hover:border-[#7A5A2E]/40' : ''}`
                  }`}
                >
                  {group.label}
                  <ChevronDown size={14} className={`transition-transform duration-200 ${openMenu === group.label ? 'rotate-180' : ''}`} />
                </button>
                {openMenu === group.label && (
                  <div className="absolute top-full left-0 pt-3">
                    <div className="min-w-[340px] bg-[#FDF8F3] border border-[#E5DDD0] rounded-xl shadow-[0_24px_60px_-20px_rgba(42,32,24,0.35)] p-2">
                      {group.items.map((it) =>
                        it.to ? (
                          <a
                            key={it.label}
                            href={it.to}
                            onClick={(e) => handleNav(e, it.to)}
                            data-testid={`nav-item-${it.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                            aria-current={pathname === it.to ? 'page' : undefined}
                            className={`block px-3 py-2.5 rounded-lg transition-colors border-l-2 ${pathname === it.to ? 'bg-[#F1E8DA] border-[#7A5A2E]' : 'border-transparent hover:bg-[#F1E8DA]'}`}
                          >
                            <div className="flex items-center justify-between gap-4">
                              <span className={`text-[14px] font-medium ${pathname === it.to ? 'text-[#1F1812]' : 'text-[#2D2520]'}`}>{it.label}</span>
                              {it.flag && <FlagBadge label={it.flag} />}
                            </div>
                            {it.desc && <p className="mt-0.5 text-[#5C4F42] text-[12px] leading-snug">{it.desc}</p>}
                          </a>
                        ) : (
                          <div key={it.label} className="flex items-center justify-between gap-4 px-3 py-2.5 rounded-lg opacity-60 cursor-default select-none">
                            <span className="text-[#5C4F42] text-[14px]">{it.label}</span>
                            <SoonBadge />
                          </div>
                        )
                      )}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <a
                key={group.label}
                href={group.to}
                onClick={(e) => handleNav(e, group.to)}
                data-testid={`nav-${group.label.toLowerCase()}`}
                aria-current={isActive(group) ? 'page' : undefined}
                className={`text-[14px] font-medium tracking-wide transition-colors border-b-2 pb-0.5 ${
                  isActive(group)
                    ? 'text-[#1F1812] border-[#7A5A2E]'
                    : 'text-[#5C4F42] hover:text-[#2D2520] border-transparent hover:border-[#7A5A2E]/40'
                }`}
              >
                {group.label}
              </a>
            )
          )}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a
            href="https://calendly.com/hello-archen"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-book-demo-btn"
            className="bg-[#2D2520] text-white hover:bg-[#1F1812] text-[13px] font-medium px-5 py-2.5 rounded-md transition-all duration-200 hover:shadow-lg hover:shadow-black/15"
          >
            Talk to the founders
          </a>
        </div>

        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="md:hidden text-[#2D2520] p-1"
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {isMobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile nav */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-[#FDF8F3] border-t border-[#E5DDD0]/60 max-h-[80vh] overflow-y-auto">
          <nav className="max-w-[1240px] mx-auto px-6 py-5 flex flex-col gap-5">
            {NAV.map((group) =>
              group.items ? (
                <div key={group.label}>
                  {group.to ? (
                    <a href={group.to} onClick={(e) => handleNav(e, group.to)} className="inline-block text-[#7A5A2E] text-[11px] tracking-[0.16em] uppercase mb-2.5">{group.label}</a>
                  ) : (
                    <p className="text-[#7A5A2E] text-[11px] tracking-[0.16em] uppercase mb-2.5">{group.label}</p>
                  )}
                  <div className="flex flex-col gap-1 pl-1">
                    {group.items.map((it) =>
                      it.to ? (
                        <a
                          key={it.label}
                          href={it.to}
                          onClick={(e) => handleNav(e, it.to)}
                          aria-current={pathname === it.to ? 'page' : undefined}
                          className={`flex items-center justify-between py-1.5 pl-2 border-l-2 text-[15px] font-medium ${pathname === it.to ? 'border-[#7A5A2E] text-[#1F1812]' : 'border-transparent text-[#2D2520]'}`}
                        >
                          {it.label}
                          {it.flag && <FlagBadge label={it.flag} />}
                        </a>
                      ) : (
                        <div key={it.label} className="flex items-center justify-between py-1.5 text-[#5C4F42] text-[15px] opacity-60">
                          {it.label}
                          <SoonBadge />
                        </div>
                      )
                    )}
                  </div>
                </div>
              ) : (
                <a
                  key={group.label}
                  href={group.to}
                  onClick={(e) => handleNav(e, group.to)}
                  className="text-[#2D2520] text-base font-medium"
                >
                  {group.label}
                </a>
              )
            )}
            <a
              href="https://calendly.com/hello-archen"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="mobile-book-demo-btn"
              onClick={() => setIsMobileMenuOpen(false)}
              className="bg-[#2D2520] text-white text-center text-base font-medium px-4 py-3 rounded-md mt-1"
            >
              Talk to the founders
            </a>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
